import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Check, ChevronDown, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TruncatedTooltip } from './TruncatedTooltip';

export interface UserMenuSubItem {
  code: string;
  label: string;
}

export type UserMenuItem =
  | {
      type?: 'default';
      label: string;
      icon: LucideIcon;
      onSelect: () => void;
      variant?: 'default' | 'danger';
      dividerBefore?: boolean;
      hidden?: boolean;
    }
  | {
      type: 'submenu';
      label: string;
      icon: LucideIcon;
      subItems: UserMenuSubItem[];
      activeSubItemCode?: string;
      onSubItemSelect: (code: string) => void;
      dividerBefore?: boolean;
      hidden?: boolean;
    };

export interface UserMenuDropdownProps {
  imageSrc?: string | null;
  initials?: string;
  avatarClassName?: string;
  triggerLabel?: string;
  name?: string;
  subtitle?: string;
  items: UserMenuItem[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentClassName?: string;
  itemClassName?: string;
  dangerItemClassName?: string;
  triggerClassName?: string;
  nameClassName?: string;
  subtitleClassName?: string;
  avatarPosition?: 'start' | 'end';
  showChevron?: boolean;
  chevronClassName?: string;
}

export function UserMenuDropdown({
  imageSrc,
  initials,
  avatarClassName,
  triggerLabel = 'User menu',
  name,
  subtitle,
  items,
  open,
  onOpenChange,
  contentClassName,
  itemClassName,
  dangerItemClassName,
  triggerClassName,
  nameClassName,
  subtitleClassName,
  avatarPosition = 'end',
  showChevron = false,
  chevronClassName,
}: UserMenuDropdownProps) {
  const baseItem =
    itemClassName ??
    'flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer outline-none select-none hover:bg-[var(--brand-accent)] hover:text-[var(--brand-primary)] transition-colors';

  const dangerItem =
    dangerItemClassName ??
    'flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer outline-none select-none text-red-500 hover:bg-red-50 hover:text-red-500 transition-colors';

  const details = (name || subtitle) && (
    <div className="flex flex-col items-start min-w-0">
      {name && (
        <span
          className={
            nameClassName ??
            'text-sm font-semibold text-slate-900 leading-[1.3] whitespace-nowrap overflow-hidden text-ellipsis max-w-[5rem] sm:max-w-[10rem]'
          }
        >
          {name}
        </span>
      )}
      {subtitle && (
        <TruncatedTooltip
          text={subtitle}
          lines={1}
          maxWidth="12rem"
          className={
            subtitleClassName ??
            'text-xs font-normal text-slate-500 leading-[1.3] text-left'
          }
        />
      )}
    </div>
  );

  const avatar = (
    <div
      className={
        avatarClassName ??
        'h-10 w-10 rounded-full bg-[#e8eaff] flex items-center justify-center text-sm font-medium shrink-0 overflow-hidden'
      }
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          className="w-full h-full object-cover"
          alt="Profile"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <span className="text-[var(--brand-primary)]">{initials}</span>
      )}
    </div>
  );

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className={
            triggerClassName ??
            'p-0 pl-1 border-none bg-transparent cursor-pointer flex items-center gap-3 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] rounded-md'
          }
          aria-label={triggerLabel}
        >
          {avatarPosition === 'start' ? (
            <>
              {avatar}
              {details}
            </>
          ) : (
            <>
              {details}
              {avatar}
            </>
          )}
          {showChevron && (
            <ChevronDown
              size={18}
              className={chevronClassName}
              aria-hidden="true"
            />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={cn('min-w-40', contentClassName)}
        align="end"
        sideOffset={8}
      >
        {items
          .filter((item) => !item.hidden)
          .map((item, index) => {
            const Icon = item.icon;
            const isDanger =
              item.type !== 'submenu' && item.variant === 'danger';

            return (
              <div key={index}>
                {item.dividerBefore && <DropdownMenuSeparator />}

                {item.type === 'submenu' ? (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className={baseItem}>
                      <Icon size={15} className="text-gray-500" />
                      {item.label}
                    </DropdownMenuSubTrigger>

                    <DropdownMenuSubContent className="min-w-40">
                      {item.subItems.map(({ code, label }) => {
                        const isActive =
                          item.activeSubItemCode?.startsWith(code);

                        return (
                          <DropdownMenuItem
                            key={code}
                            className={baseItem}
                            onSelect={() => item.onSubItemSelect(code)}
                          >
                            <span className="flex w-4 shrink-0 items-center justify-center">
                              {isActive && <Check className="size-3.5" />}
                            </span>
                            <span
                              className={isActive ? 'font-semibold' : undefined}
                            >
                              {label}
                            </span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ) : (
                  <DropdownMenuItem
                    className={isDanger ? dangerItem : baseItem}
                    onSelect={item.onSelect}
                  >
                    <Icon size={15} />
                    {item.label}
                  </DropdownMenuItem>
                )}
              </div>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
